import { useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * useGameTick — sends a "game tick" every 60s while a game session is active
 * AND the user has interacted with the page in the last 60 seconds.
 * The server awards 1 Spark per accepted tick (rate-limited to 1/min server-side).
 *
 * Idle detection: any of mousemove / click / keydown / touchstart / wheel resets
 * the activity timer. If no interaction in the last 60s (i.e. idle), the tick is
 * skipped — no Sparks awarded while AFK.
 *
 * Usage:
 *   useGameTick("slots");                             // single-player
 *   useGameTick("chess", channel?.username);          // multiplayer inside a channel
 */
const ACTIVITY_EVENTS = ["mousemove", "click", "keydown", "touchstart", "wheel"];
const IDLE_THRESHOLD_MS = 60_000; // 60s of no input = idle
const TICK_INTERVAL_MS  = 60_000; // fire once a minute

export default function useGameTick(gameSlug, channelUsername = null) {
  const { user, refreshMe } = useAuth();

  useEffect(() => {
    if (!user || !gameSlug) return;
    let lastActivity = Date.now();
    let cancelled = false;

    const onActivity = () => { lastActivity = Date.now(); };
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));

    const tick = async () => {
      // Only accrue Sparks if the user is actively interacting
      if (Date.now() - lastActivity > IDLE_THRESHOLD_MS) return;
      // Also skip if the tab is hidden — reinforces "actively playing" semantics
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const r = await api.post("/games/tick", { game_slug: gameSlug, channel_username: channelUsername || null });
        if (!cancelled && r?.data?.sparks_awarded > 0) refreshMe();
      } catch { /* ignore */ }
    };

    const interval = setInterval(tick, TICK_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameSlug, channelUsername, user?.username]);
}
